import { defineStore } from "pinia";
import cookies from "@/services/storage/cookie.client";
import apiCheckoutService from "@/services/http/api-drive/magasin/checkout";
import apiMagasinModePaiementService from "@/services/http/api-drive/magasin/modePaiement";
import { useMagasinStore } from './magasin';
import { useContextStore } from './context';
import { usePanierStore } from './panier';

export const StatusTypes = {
  CHECKOUT_REQUEST: 'CHECKOUT_REQUEST',
  CHECKOUT_SUCCESS: 'CHECKOUT_SUCCESS',
  CHECKOUT_ERROR: 'CHECKOUT_ERROR'
}

export const ActionTypes = {
  CREATE: "CREATE",
  UPDATE_MODE_COMMANDE: "UPDATE_MODE_COMMANDE",
  UPDATE_MODE_PAIEMENT: "UPDATE_MODE_PAIEMENT",
  FETCH_MODES_PAIEMENT: "FETCH_MODES_PAIEMENT",
  UPDATE_EUROCORA: "UPDATE_EUROCORA",
  PROCESS_FORM_REDEVANCE: "PROCESS_FORM_REDEVANCE",
  PROCESS_CODE_PROMO: "PROCESS_CODE_PROMO",
  PROCESS_AVANTAGES: "PROCESS_AVANTAGES",
  PROCESS_PAIEMENT: "PROCESS_PAIEMENT"
}

export const keyMagasinInhabituel = '__cora__mi__';
export const keyPremiereCommande = '__cora__pc__';

export const useCheckoutStore = defineStore({
  id: 'checkout',
  state: () => {
    return {
      checkout: null,
      meta: null,
      urlPaiement: null,
      status: {
        type: null,
        action: null
      },
      error: null,
      step: {
        mode: 'default',
        value: 1
      },
      addresseLad: null,
      popinProduitsIncompatibles: {
        modeCommande: null,
        visible: false
      },
      produitsIncompatibles: null,
      avantagesCalcules: false,
      typePanier: "drive",
      alertSmsPhone: null,
      modesPaiement: [],
      modesPaiementInfos: null,
      isCarteCarrefourSelected: false,
      selectedPaymentId: null
    }
  },
  actions: {
    checkoutRequest(action) {
      this.status = {
        type: StatusTypes.CHECKOUT_REQUEST,
        action
      };
    },
    checkoutSuccess(action) {
      this.status = {
        type: StatusTypes.CHECKOUT_SUCCESS,
        action
      };
      if (action === ActionTypes.PROCESS_AVANTAGES) {
        this.avantagesCalcules = true;
      }
    },
    checkoutError(action) {
      this.status = {
        type: StatusTypes.CHECKOUT_ERROR,
        action
      };
    },
    setCheckout(checkout) {
      this.checkout = checkout;
    },
    updateCheckoutData(updatedAttributes) {
      this.checkout.attributes = { ...this.checkout.attributes, ...updatedAttributes };
    },
    setCheckoutMeta(meta) {
      this.meta = meta;
    },
    setUrlPaiement(url) {
      this.urlPaiement = url;
    },
    setError(error) {
      this.error = error;
    },
    setStepMode(mode) {
      this.step.mode = mode;
    },
    setStepValue(value) {
      this.step.value = value;
    },
    resetError() {
      this.error = null;
      this.status = {
        type: null,
        action: null
      };
    },
    setAlertSmsPhone(value) {
      this.alertSmsPhone = value;
    },
    setAddresseLad(addresseLad) {
      this.checkout.attributes = { ...this.checkout.attributes, ...{ idAdresseLivraison: addresseLad.id } };
      this.addresseLad = addresseLad;
    },
    resetAddresseLad() {
      this.addresseLad = null;
      if (this.checkout) {
        this.checkout.attributes.idAdresseLivraison = null;
      }
    },
    resetCheckout() {
      this.checkout = null;
      this.meta = null;
      this.error = null;
      this.addresseLad = null;
      this.alertSmsPhone = null;
      this.status = {
        type: null,
        action: null
      };
      this.avantagesCalcules = false;
    },
    setPopinProduitsIncompatiblesVisible(visible) {
      this.popinProduitsIncompatibles.visible = visible;
    },
    setPopinProduitsIncompatiblesModeCommande(modeCommande) {
      this.popinProduitsIncompatibles.modeCommande = modeCommande;
    },
    setProduitsIncompatibles(produits) {
      this.produitsIncompatibles = produits;
    },
    resetWarning(key) {
      if (this.meta && this.meta.warning && typeof this.meta.warning[key] !== 'undefined') {
        this.meta.warning[key] = false;
        if (process.client) {
          if (key === "magasin_inhabituel" && this.checkout && this.checkout.attributes.magasin_id) {
            cookies.set(keyMagasinInhabituel + this.checkout.attributes.magasin_id, true, {
              expires: 1
            });
          }
          if (key === "premiere_commande") {
            cookies.set(keyPremiereCommande, true, {
              expires: 1
            });
          }
        }
      }
    },
    setTypePanier(value) {
      this.typePanier = value;
    },
    getProduitsIndisposForDay(day) {
      if (!this.checkout.attributes.panier) {
        return []
      }
      return this.checkout.attributes.panier.data.attributes.lignes_panier.data.filter(produit => produit.attributes.produit.data.attributes.jours_retraits && produit.attributes.produit.data.attributes.jours_retraits.find(jour => jour.jour_numero === day && !jour.disponible))
    },
    async create() {
      const magasinStore = useMagasinStore();
      const contextStore = useContextStore();
      const panierStore = usePanierStore();

      this.checkoutRequest(ActionTypes.CREATE);
      try {
        const { data, meta } = await this.$apiDrive.use(apiCheckoutService).create(magasinStore.id, contextStore.uuid, this.typePanier, panierStore.dateLastUpdate);

        this.setCheckout(data);
        this.resetAddresseLad();
        this.setCheckoutMeta(meta);
        this.checkoutSuccess(ActionTypes.CREATE);
      } catch (error) {
        this.setError(this.$apiDrive.getErrorData(error));
        this.checkoutError(ActionTypes.CREATE);
      }
    },
    async updateModeCommande(data) {
      this.checkoutRequest(ActionTypes.UPDATE_MODE_COMMANDE);
      try {
        const res = await this.update(data);
        const updatedAttributes = {};
        if (res.dateLastUpdate) { updatedAttributes.dateLastUpdate = res.dateLastUpdate; }
        if (data.dateRetrait) { updatedAttributes.dateRetrait = data.dateRetrait; }
        if (data.service_id) { updatedAttributes.service_id = data.service_id; }
        if (data.idAdresseLivraison) { updatedAttributes.idAdresseLivraison = data.idAdresseLivraison; }
        if (data.tirelire) { updatedAttributes.tirelire = data.tirelire; }
        if (res.checkout && res.checkout.data.attributes.panier) { updatedAttributes.panier = res.checkout.data.attributes.panier; }
        if (data.avec_sacs) { updatedAttributes.avec_sacs = data.avec_sacs; }

        this.updateCheckoutData(updatedAttributes);
        this.checkoutSuccess(ActionTypes.UPDATE_MODE_COMMANDE);
        return Promise.resolve();
      } catch (error) {
        const apiError = this.$apiDrive.getErrorData(error);
        this.setError(apiError);
        this.checkoutError(ActionTypes.UPDATE_MODE_COMMANDE);
        return Promise.reject(apiError);
      }
    },
    async updateModePaiement(data) {
      this.checkoutRequest(ActionTypes.UPDATE_MODE_PAIEMENT);
      try {
        const res = await this.update(data);
        const updatedAttributes = {};
        if (res.dateLastUpdate) { updatedAttributes.dateLastUpdate = res.dateLastUpdate; }
        if (data.modePaiement) { updatedAttributes.modePaiement = data.modePaiement; }
        this.updateCheckoutData(updatedAttributes);
        this.checkoutSuccess(ActionTypes.UPDATE_MODE_PAIEMENT);
      } catch (error) {
        this.setError(this.$apiDrive.getErrorData(error));
        this.checkoutError(ActionTypes.UPDATE_MODE_PAIEMENT);
      }
    },
    async updateCarteCadeau(data) {
      this.resetError();
      this.checkoutRequest(ActionTypes.UPDATE_MODE_PAIEMENT);
      try {
        // TODO MOCK
        const res = await this.update(data);
        const updatedAttributes = {};
        if (res.dateLastUpdate) { updatedAttributes.dateLastUpdate = res.dateLastUpdate; }
        this.selectedPaymentId = null
        updatedAttributes.carteCarrefour = res.checkout && res.checkout.data.attributes.carteCarrefour ? res.checkout.data.attributes.carteCarrefour : undefined;
        updatedAttributes.panier = res.checkout && res.checkout.data.attributes.panier ? res.checkout.data.attributes.panier : undefined;
        this.updateCheckoutData(updatedAttributes);
        this.checkoutSuccess(ActionTypes.UPDATE_MODE_PAIEMENT);
        return Promise.resolve();
      } catch (error) {
        const apiError = this.$apiDrive.getErrorData(error);
        this.setError(apiError);
        this.checkoutError(ActionTypes.UPDATE_MODE_PAIEMENT);
        return Promise.reject(apiError);

      }
    },
    async update(data) {
      const magasinStore = useMagasinStore();
      const res = await this.$apiDrive.use(apiCheckoutService).update(magasinStore.id, this.id, data);
      return res;
    },
    async processFormRedevance(formRedevance) {
      const magasinStore = useMagasinStore();
      this.checkoutRequest(ActionTypes.PROCESS_FORM_REDEVANCE);
      try {
        const { data } = await this.$apiDrive.use(apiCheckoutService).processFormRedevance(magasinStore.id, this.id, this.dateLastUpdate, formRedevance);
        if (data.attributes.dateLastUpdate) {
          this.updateCheckoutData({ dateLastUpdate: data.attributes.dateLastUpdate });
        }
        this.checkoutSuccess(ActionTypes.PROCESS_FORM_REDEVANCE);
        return Promise.resolve();
      } catch (error) {
        const apiError = this.$apiDrive.getErrorData(error);
        this.setError(apiError);
        this.checkoutError(ActionTypes.PROCESS_FORM_REDEVANCE);
        return Promise.reject(apiError);
      }
    },
    async processCodePromo(codePromo) {
      const magasinStore = useMagasinStore();
      this.checkoutRequest(ActionTypes.PROCESS_CODE_PROMO);
      try {
        const { data } = await this.$apiDrive.use(apiCheckoutService).processCodePromo(magasinStore.id, this.id, this.dateLastUpdate, codePromo);
        const updatedAttributes = {};
        if (data.attributes.dateLastUpdate) { updatedAttributes.dateLastUpdate = data.attributes.dateLastUpdate; }
        if (data.attributes.panier) { updatedAttributes.panier = data.attributes.panier; }

        this.updateCheckoutData(updatedAttributes);
        this.checkoutSuccess(ActionTypes.PROCESS_CODE_PROMO);
        return Promise.resolve();
      } catch (error) {
        const apiError = this.$apiDrive.getErrorData(error);
        this.setError(apiError);
        this.checkoutError(ActionTypes.PROCESS_CODE_PROMO);
        return Promise.reject(apiError);
      }
    },
    async processAvantages() {
      const magasinStore = useMagasinStore();
      this.checkoutRequest(ActionTypes.PROCESS_AVANTAGES);
      try {
        const res = await this.$apiDrive.use(apiCheckoutService).processAvantages(magasinStore.id, this.id, this.dateLastUpdate);
        const data = res.data;
        const updatedAttributes = {};
        if (data.attributes.dateLastUpdate) { updatedAttributes.dateLastUpdate = data.attributes.dateLastUpdate; }
        if (data.attributes.panier) { updatedAttributes.panier = data.attributes.panier; }

        this.updateCheckoutData(updatedAttributes);
        this.checkoutSuccess(ActionTypes.PROCESS_AVANTAGES);
        return Promise.resolve(res);
      } catch (error) {
        const apiError = this.$apiDrive.getErrorData(error);
        this.setError(apiError);
        this.checkoutError(ActionTypes.PROCESS_AVANTAGES);
        return Promise.reject(apiError);
      }
    },
    async processPaiement() {
      const magasinStore = useMagasinStore();
      this.checkoutRequest(ActionTypes.PROCESS_PAIEMENT);
      try {
        const data = await this.$apiDrive.use(apiCheckoutService).processPaiement(magasinStore.id, this.id, this.dateLastUpdate);
        if (data.dateLastUpdate) {
          this.updateCheckoutData({ dateLastUpdate: data.dateLastUpdate });
        }
        this.setUrlPaiement(data.url_webview_paiement);
        this.checkoutSuccess(ActionTypes.PROCESS_PAIEMENT);
        return Promise.resolve(data);
      } catch (error) {
        const apiError = this.$apiDrive.getErrorData(error);
        this.setError(apiError);
        this.checkoutError(ActionTypes.PROCESS_PAIEMENT);
        return Promise.reject(apiError);
      }
    },
    async processPaiementCarteKdo() {
      const magasinStore = useMagasinStore();
      this.checkoutRequest(ActionTypes.PROCESS_PAIEMENT);
      try {
        const data = await this.$apiDrive.use(apiCheckoutService).processPaiementCarteCadeau(magasinStore.id, this.id, this.dateLastUpdate);
        this.checkoutSuccess(ActionTypes.PROCESS_PAIEMENT);
        return Promise.resolve(data);
      } catch (error) {
        const apiError = this.$apiDrive.getErrorData(error);
        this.setError(apiError);
        this.checkoutError(ActionTypes.PROCESS_PAIEMENT);
        return Promise.reject(apiError);
      }
    },
    async fetchModesPaiement() {
      const magasinStore = useMagasinStore();
      this.checkoutRequest(ActionTypes.FETCH_MODES_PAIEMENT);
      try {
        const res = await this.$apiDrive.use(apiMagasinModePaiementService).modePaiements(magasinStore.id, this.id);
        this.modesPaiement = res.data;
        this.modesPaiementInfos = res.meta.infos;
        this.checkoutSuccess(ActionTypes.FETCH_MODES_PAIEMENT);
        return Promise.resolve();
      } catch (error) {
        const apiError = this.$apiDrive.getErrorData(error);
        this.setError(this.$apiDrive.getErrorData(error));
        this.checkoutError(ActionTypes.FETCH_MODES_PAIEMENT);
        return Promise.reject(apiError);
      }
    },
  },
  getters: {
    id: (state) => {
      return state.checkout ? state.checkout.id : null;
    },
    restantAPayer: (state) => {
      const montantTotal = state.checkout && state.checkout.attributes.panier ? parseFloat(state.checkout.attributes.panier.data.attributes.montant_total_ttc) : 0;
      const carteCadeauAmount = state.checkout?.attributes?.carteCarrefour?.montant

      return montantTotal - (carteCadeauAmount || 0);
    },
    dateLastUpdate: (state) => {
      return state.checkout ? state.checkout.attributes.dateLastUpdate : null;
    },
    modeCommande: (state) => {
      return state.checkout
        ? state.checkout.attributes.modeCommandeLibelle
        : null;
    },
    dateRetrait: (state) => {
      return state.checkout ? state.checkout.attributes.dateRetrait : null;
    },
    montantTotalTtc: (state) => {
      return state.checkout && state.checkout.attributes.panier
        ? parseFloat(
          state.checkout.attributes.panier.data.attributes.montant_total_ttc
        )
        : 0;
    },
    montantTotalTtcSansRsa: (state) => {
      return state.checkout && state.checkout.attributes.panier
        ? parseFloat(
          state.checkout.attributes.panier.data.attributes.montant_total_ttc_sans_rsa
        )
        : 0;
    },
    produits: (state) => {
      return state.checkout && state.checkout.attributes.panier
        ? state.checkout.attributes.panier.data.attributes.lignes_panier.data
        : [];
    },
    nbProduits: (state) => {
      if (!state.checkout) {
        return 0;
      }
      return state.checkout.attributes.panier.data.attributes.lignes_panier.data.reduce(
        (acc, current) => {
          const next = (acc += parseInt(current.attributes.quantite));
          return next;
        },
        0
      );
    },
    carteCadeau: (state) => {
      // TODO : MOCK
      return state.checkout?.attributes?.carteCarrefour
    },
    isCarrefourCagnottePayment: (state) => {
      return !!state.checkout?.attributes?.carteCarrefour
    },
    idAdresseLivraison: (state) => {
      return state.checkout ? state.checkout.attributes.idAdresseLivraison : null;
    },
    serviceId: (state) => {
      return state.checkout ? state.checkout.attributes.service_id : null;
    },
    consignesSacs: (state) => {
      return state.checkout && state.checkout.attributes.panier && state.checkout.attributes.panier.data.attributes.options_incluses
        ?
        state.checkout.attributes.panier.data.attributes.options_incluses.find(option => option.type === "CSAC")
        : null;
    },
    idModePaiementHabituel: (state) => {
      return state.meta && state.meta.user_preferences
        ? state.meta.user_preferences.id_mode_paiement_habituel
        : null;
    },
    warning: (state) => {
      return state.meta ? state.meta.warning : null;
    },
    premiereConsigneSac: (state) => {
      return state.meta && state.meta.user_preferences
        ? state.meta.user_preferences.premiere_consigne_sac
        : null;
    },
    redevanceItems: (state) => {
      return state.meta &&
        state.meta.warning &&
        state.meta.warning.redevance &&
        state.meta.warning.redevance.length
        ? state.meta.warning.redevance
        : null;
    },
    isPremiereCommande: (state) => {
      const isWarning =
        state.meta && state.meta.warning && state.meta.warning.premiere_commande
          ? state.meta.warning.premiere_commande
          : false;
      if (process.client && isWarning && cookies.get(keyPremiereCommande)) {
        return false;
      }
      return isWarning;
    },
    isMagasinInhabituel: (state) => {
      const isWarning =
        state.meta && state.meta.warning && state.meta.warning.magasin_inhabituel
          ? state.meta.warning.magasin_inhabituel
          : false;
      if (
        process.client &&
        isWarning &&
        state.checkout &&
        cookies.get(keyMagasinInhabituel + state.checkout.attributes.magasin_id)
      ) {
        return false;
      }
      return isWarning;
    },
    panier: (state) => {
      return state.checkout
        ? state.checkout.attributes.panier.data.attributes
        : null;
    },
    typeModeCommandeHabituel: (state) => {
      return state.meta && state.meta.user_preferences
        ? state.meta.user_preferences.type_mode_commande_habituel
        : null;
    },
    modesCommandes: (state) => {
      return state.meta && state.meta.modesCommande
        ? state.meta.modesCommande
        : null;
    },
    getInfosModeCommande: (state) => (type) => {
      if (state.meta && state.meta.modesCommande) {
        if (
          state.meta.modesCommande.infos &&
          state.meta.modesCommande.infos[type]
        ) {
          return state.meta.modesCommande.infos[type];
        }
      }

      return null;
    },
    isModeCommandeDisabled: (state) => (modeCommande) => {
      if (
        state.meta &&
        state.meta.modesCommande &&
        state.meta.modesCommande.services
      ) {
        const nbImpossible = modeCommande.services.reduce((acc, current) => {
          const serviceId = current.id;
          if (
            state.meta.modesCommande.services[serviceId] &&
            !state.meta.modesCommande.services[serviceId].possible
          ) {
            return acc + 1;
          }
          return acc;
        }, 0);
        return nbImpossible === modeCommande.services.length;
      }
      return false;
    },
    getCurrentPaymentMethod: (state) => {
      return state.selectedPaymentId;
    },
    getInfosPossibleModeCommande: (state) => (modeCommande) => {
      if (
        state.meta &&
        state.meta.modesCommande &&
        state.meta.modesCommande.services
      ) {
        if (modeCommande.services.length > 0) {
          const serviceId = modeCommande.services[0].id;
          if (state.meta.modesCommande.services[serviceId]) {
            return state.meta.modesCommande.services[serviceId];
          }
        }
      }
      return {
        possible: true
      };
    },
    getInfosPossibleService: (state) => (serviceId) => {
      if (
        state.meta &&
        state.meta.modesCommande &&
        state.meta.modesCommande.services &&
        state.meta.modesCommande.services[serviceId]
      ) {
        return state.meta.modesCommande.services[serviceId];
      }
      return {
        possible: true
      };
    },
    getInfosPossibleServices: (state) => {
      if (
        state.meta &&
        state.meta.modesCommande &&
        state.meta.modesCommande.services
      ) {
        return state.meta.modesCommande.services;
      }
      return [];
    },
    codePromo: (state) => {
      if (
        state.checkout &&
        state.checkout.attributes.panier &&
        state.checkout.attributes.panier.data.attributes.reductions &&
        state.checkout.attributes.panier.data.attributes.reductions.length
      ) {
        return (
          state.checkout.attributes.panier.data.attributes.reductions.find(
            (reduc) => reduc.type === "code_promo"
          ) || null
        );
      }
      return null;
    },
    isStatusUpdate: (state) => {
      return (
        state.status.action === ActionTypes.UPDATE_MODE_COMMANDE ||
        state.status.action === ActionTypes.UPDATE_MODE_PAIEMENT ||
        state.status.action === ActionTypes.PROCESS_AVANTAGES ||
        state.status.action === ActionTypes.PROCESS_PAIEMENT
      );
    },
    lignes: (state) => {
      return state.checkout &&
        state.checkout.attributes.panier &&
        state.checkout.attributes.panier.data.attributes.lignes_panier
        ? state.checkout.attributes.panier.data.attributes.lignes_panier.data
        : [];
    },
    lddDelaiLinkLabel: (state) => {
      if (state.typePanier !== "ldd") {
        return null;
      }
      const lignes = state.lignes;
      if (
        lignes.length === 1
      ) {
        if (
          lignes[0].attributes.produit.data.attributes.infos_vendeur
        ) {
          return `Sous ${lignes[0].attributes.produit.data.attributes.infos_vendeur.delai_livraison} jours ouvrés`;
        }
      }
      return "Voir les délais de livraison";
    },
    produitsIncompatiblesFullCb: (state) => {
      return state.checkout && state.checkout.attributes.panier
        ? state.checkout.attributes.panier.data.attributes.lignes_panier.data.filter(p => p.attributes.produit.data.attributes.eligible_fullcb === false)
        : [];
    },
    isCarteCarrefourPossible: (state) => {
      const modePaiementCarrefour = state.modesPaiement.find(mp => mp.attributes.mode_paiement === "CRF");
      if (!modePaiementCarrefour) {
        return false;
      };
      return state.modesPaiementInfos[modePaiementCarrefour.id]?.possible;
    },
  }
})